import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { Mitarbeiter } from '@chancenwerk/master-data-management/users/entities/Mitarbeiter.js';
import { Workshop } from '@chancenwerk/workshops/entities/Workshop.js';
import { WorkshopAppointment } from '@chancenwerk/workshops/entities/WorkshopAppointment.js';
import type { WorkshopAttendanceStatus } from '@chancenwerk/workshops/entities/WorkshopAttendanceStatus.js';
import { DateTime } from 'luxon';

/**
 * @entity WorkshopAttendance
 */
@Entity()
export class WorkshopTrainingAttendance extends MongoEntity {
  public static override _class = 'WorkshopTrainingAttendance';
  public static override _collectionName = 'workshop-attendance';

  @DataLoadedField(type => Mitarbeiter, ({ userID }) => userID)
  public user: Maybe<Mitarbeiter>;

  @Field(type => String)
  public userID: Maybe<string>;

  @Field(type => DateTime, {
    default: val => val ?? DateTime.now(),
  })
  public createdAt: Maybe<DateTime>;

  @DataLoadedField(type => Workshop, ({ workshopID }) => workshopID)
  public workshop: Maybe<Workshop>;

  @validate(nonOptional())
  @Field(type => String)
  public workshopID: Maybe<string>;

  @Field(type => DateTime)
  public date: Maybe<DateTime>;

  @Field(type => String)
  public state: Maybe<WorkshopAttendanceStatus>;

  @Field(type => String)
  public accessToken: Maybe<string>;

  @Field(type => WorkshopAppointment, {
    resolve: function (this: EntityData<WorkshopTrainingAttendance>) {
      return async () => {
        const date = this.date instanceof DateTime ? this.date.toJSDate() : this.date;
        const col = await getRawCollection<EntityData<WorkshopAppointment>>(WorkshopAppointment._collectionName);
        const res = (await col.findOne({
          workshopID: this.workshopID,
          $and: [
            {
              start: {
                $gte: DateTime.fromJSDate(date ?? new Date())
                  .startOf('day')
                  .toJSDate(),
              },
            },
            {
              start: {
                $lte: DateTime.fromJSDate(date ?? new Date())
                  .endOf('day')
                  .toJSDate(),
              },
            },
          ],
        })) as WorkshopAppointment;
        return res;
      };
    },
  })
  public appointment: Maybe<WorkshopAppointment>;
}

if (A.isServer) {
  const attendance = getRawCollection<any>(WorkshopTrainingAttendance._collectionName);

  // Create DB Indices
  void attendance.then(async db => {
    void db.createIndex({ userID: 1, workshopID: 1 });
  });
}
